import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  lessonCallsApi,
  type LessonCallApiError,
  type LessonCallSession,
  type LessonCallType,
  type WebrtcConfig,
} from '@/api/lessonCalls'
import {
  CallContext,
  type CallContextValue,
  type CallParticipantView,
  type CallStatus,
  type LessonCallStateEvent,
} from './callContext'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { joinCallChannel, type CallChannel, type CallSignalMessage } from './callSignaling'
import { usePeerConnections } from './usePeerConnections'
import { useLocalMedia } from './useLocalMedia'
import { useVoiceActivity } from './useVoiceActivity'

/**
 * Состояние звонка на странице урока: REST для жизненного цикла, whisper для
 * SDP/ICE, presence-канал урока для оповещения о входящем.
 *
 * Провайдер монтируется всегда, а звонки включает проп `enabled` (идущий урок):
 * условный монтаж менял бы тип элемента-обёртки и пересоздавал всю страницу.
 */

type CallProviderProps = {
  /** Звонки возможны — урок идёт. При false провайдер не создаёт подписок. */
  enabled: boolean
  lessonId: number
  selfId: number
  /** Учитель урока (и админ) может начинать и завершать звонок для всех. */
  canManage: boolean
  /**
   * Последнее состояние звонка из presence-канала урока (`lesson.call.state`).
   * Приходит пропом, а не второй подпиской на тот же канал: у Echo канал один
   * на имя, и отписка одного потребителя убила бы подписку другого.
   */
  callState: LessonCallStateEvent | null
  children: ReactNode
}

export function CallProvider({
  enabled,
  lessonId,
  selfId,
  canManage,
  callState,
  children,
}: CallProviderProps) {
  const { t } = useTranslation()
  const [status, setStatus] = useState<CallStatus>('idle')
  const [session, setSession] = useState<LessonCallSession | null>(null)
  const [config, setConfig] = useState<WebrtcConfig | null>(null)
  const [remoteStreams, setRemoteStreams] = useState<Record<number, MediaStream>>({})
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)
  const [connectionStates, setConnectionStates] = useState<Record<number, RTCPeerConnectionState>>({})
  const [channelUsers, setChannelUsers] = useState<Record<number, string>>({})
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [quality, setQuality] = useState<number | null>(null)

  const channelRef = useRef<CallChannel | null>(null)
  const declinedSessionRef = useRef<number | null>(null)
  const sessionRef = useRef<LessonCallSession | null>(null)
  const configRef = useRef<WebrtcConfig | null>(null)
  const statusRef = useRef<CallStatus>('idle')
  const heartbeatRef = useRef<number | null>(null)
  const durationRef = useRef<number | null>(null)
  const statsRef = useRef<number | null>(null)
  /**
   * Номер «поколения» звонка. Растёт на каждом teardown: запрос микрофона
   * может резолвиться уже после ухода со страницы, и без сверки поколения
   * поток, таймеры и подписка на канал остались бы жить без владельца.
   */
  const generationRef = useRef(0)

  sessionRef.current = session
  configRef.current = config
  statusRef.current = status

  const media = useLocalMedia()
  /**
   * Уровни голоса живут в ref, а не в state: они меняются ~15 раз в секунду,
   * и через состояние перерисовывали бы всех потребителей контекста весь звонок.
   * Подписчики (аватары) читают их сами и пишут в CSS-переменную.
   */
  const voice = useVoiceActivity()

  const handleRemoteStream = useCallback(
    (userId: number, stream: MediaStream) => {
      setRemoteStreams((prev) => ({ ...prev, [userId]: stream }))
      voice.trackStream(userId, stream)
    },
    [voice],
  )

  const handlePeerClosed = useCallback(
    (userId: number) => {
      setRemoteStreams((prev) => {
        const next = { ...prev }
        delete next[userId]
        return next
      })
      setConnectionStates((prev) => {
        const next = { ...prev }
        delete next[userId]
        return next
      })
      voice.untrackStream(userId)
    },
    [voice],
  )

  const peers = usePeerConnections({
    selfId,
    getIceServers: () => configRef.current?.ice_servers ?? [],
    getVideoMaxBitrate: () => configRef.current?.video.constraints?.max_bitrate ?? null,
    getLocalStream: media.getStream,
    sendSignal: (message: CallSignalMessage) => channelRef.current?.sendSignal(message),
    onRemoteStream: handleRemoteStream,
    onPeerClosed: handlePeerClosed,
    onConnectionStateChange: (userId, state) => {
      setConnectionStates((prev) => ({ ...prev, [userId]: state }))

      // Соединение не удалось даже через TURN — почти всегда сеть режет UDP и
      // TCP-relay; общий текст ошибки тут пользователю ничего не объясняет.
      if (state === 'failed') {
        setError(t('lessonCall.errors.networkBlocked'))
      }
    },
  })

  const peersRef = useRef(peers)
  peersRef.current = peers

  const stopTimers = useCallback(() => {
    if (heartbeatRef.current !== null) {
      window.clearInterval(heartbeatRef.current)
      heartbeatRef.current = null
    }
    if (durationRef.current !== null) {
      window.clearInterval(durationRef.current)
      durationRef.current = null
    }
    if (statsRef.current !== null) {
      window.clearInterval(statsRef.current)
      statsRef.current = null
    }
  }, [])

  /** Полный сброс медиа-части: соединения, микрофон, анализаторы, таймеры. */
  const teardownMedia = useCallback(() => {
    // Поколение растёт первым: всё, что сейчас «в полёте» (запрос микрофона,
    // обработка сигнала), увидит расхождение и не станет ничего оживлять.
    generationRef.current += 1

    stopTimers()
    // Из канала выходим до закрытия соединений: сигнал, пришедший в этом окне,
    // создал бы новый RTCPeerConnection уже после teardown и остался бы висеть.
    channelRef.current?.leave()
    channelRef.current = null
    peersRef.current.closeAll()
    media.stop()
    voice.reset()
    setLocalStream(null)
    setRemoteStreams({})
    setConnectionStates({})
    setChannelUsers({})
    setDuration(0)
    setQuality(null)
  }, [media, stopTimers, voice])

  /**
   * Ссылка на актуальный teardown. Сам колбэк пересоздаётся на каждом рендере
   * (useLocalMedia возвращает новый объект), и эффект размонтирования с ним в
   * зависимостях сбрасывал бы медиа при каждом тике таймера длительности.
   */
  const teardownRef = useRef(teardownMedia)
  teardownRef.current = teardownMedia

  /**
   * Ошибка API: текст берётся общим для проекта способом, иначе при 500 или
   * сетевом сбое в UI попадало бы «Request failed with status code 500» либо
   * вовсе пустая строка.
   */
  const applyApiError = useCallback(
    (err: unknown) => {
      const rich = err as LessonCallApiError
      setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
      setErrorCode(rich?.code ?? null)
    },
    [t],
  )

  /**
   * Ошибки getUserMedia приходят техническими именами (NotAllowedError) —
   * пользователю нужен понятный текст и подсказка, что делать.
   */
  const applyMediaError = useCallback(
    (err: unknown) => {
      const name = (err as { name?: string })?.name

      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setError(t('lessonCall.errors.microphoneDenied'))
        setErrorCode(null)
        return
      }
      if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        setError(t('lessonCall.errors.microphoneMissing'))
        setErrorCode(null)
        return
      }

      applyApiError(err)
    },
    [applyApiError, t],
  )

  /** Подписка на канал звонка + установка соединений с уже вошедшими. */
  const openChannel = useCallback(() => {
    if (channelRef.current) return

    channelRef.current = joinCallChannel(lessonId, {
      onHere: (users) => {
        setChannelUsers(Object.fromEntries(users.map((u) => [u.id, u.name])))
        users.filter((u) => u.id !== selfId).forEach((u) => peersRef.current.connectTo(u.id))
      },
      onJoining: (user) => {
        setChannelUsers((prev) => ({ ...prev, [user.id]: user.name }))
        if (user.id !== selfId) peersRef.current.connectTo(user.id)
      },
      onLeaving: (user) => {
        setChannelUsers((prev) => {
          const next = { ...prev }
          delete next[user.id]
          return next
        })
        peersRef.current.closePeer(user.id)
      },
      onSignal: (message) => {
        void peersRef.current.handleSignal(message)
      },
      onPeerLeave: ({ from }) => peersRef.current.closePeer(from),
      onError: (err) => console.error('[call] Ошибка канала звонка', err),
    })
  }, [lessonId, selfId])

  const startTimers = useCallback(() => {
    const intervalSeconds = configRef.current?.heartbeat_interval_seconds ?? 30

    if (heartbeatRef.current === null) {
      heartbeatRef.current = window.setInterval(() => {
        const current = sessionRef.current
        if (!current) return
        void lessonCallsApi.heartbeat(lessonId, current.id).catch(() => undefined)
      }, intervalSeconds * 1000)
    }

    if (durationRef.current === null) {
      durationRef.current = window.setInterval(() => setDuration((prev) => prev + 1), 1000)
    }

    if (statsRef.current === null) {
      statsRef.current = window.setInterval(() => {
        void peersRef.current.collectQualityStats().then((stats) => {
          if (!stats) return
          const loss = stats.packet_loss ?? 0
          const rtt = stats.rtt_ms ?? 0
          if (loss > 5 || rtt > 400) setQuality(0)
          else if (loss > 2 || rtt > 200) setQuality(1)
          else setQuality(2)
        })
      }, 10_000)
    }
  }, [lessonId])

  /**
   * Общая часть входа в звонок: медиа, канал, таймеры.
   *
   * Запрос микрофона показывает системное окно и может висеть сколько угодно.
   * Если за это время звонок свернули (ушли со страницы, урок завершили),
   * поколение изменится — полученный поток нужно немедленно погасить, иначе
   * индикатор записи останется гореть, а таймеры и подписка — работать.
   */
  const enterCall = useCallback(
    async (joined: LessonCallSession, webrtcConfig: WebrtcConfig) => {
      const generation = generationRef.current
      setStatus('connecting')

      const stream = await media.acquire({
        video: joined.type === 'video',
        quality: joined.type === 'video' ? webrtcConfig.video.constraints : null,
      })

      if (generation !== generationRef.current) {
        stream.getTracks().forEach((track) => track.stop())
        return false
      }

      setLocalStream(stream)
      voice.trackStream(selfId, stream)

      openChannel()
      startTimers()
      setStatus('active')

      return true
    },
    [media, openChannel, selfId, startTimers, voice],
  )

  const loadConfig = useCallback(async (): Promise<WebrtcConfig> => {
    const loaded = await lessonCallsApi.getWebrtcConfig(lessonId)
    setConfig(loaded)
    configRef.current = loaded
    return loaded
  }, [lessonId])

  const startCall = useCallback(
    async (type: LessonCallType = 'audio') => {
      setError(null)
      setErrorCode(null)
      let createdSessionId: number | null = null

      try {
        const webrtcConfig = configRef.current ?? (await loadConfig())
        const created = await lessonCallsApi.start(lessonId, type)
        createdSessionId = created.id
        setSession(created)
        sessionRef.current = created

        // Вход прерван (ушли со страницы) — сессию закрываем, как и при ошибке.
        if (!(await enterCall(created, webrtcConfig))) {
          await lessonCallsApi.end(lessonId, created.id).catch(() => undefined)
        }
      } catch (err) {
        applyMediaError(err)
        teardownMedia()
        setStatus('idle')

        // Сессия уже создана, а войти не удалось (например, отказали в доступе
        // к микрофону): без закрытия она осталась бы активной и блокировала
        // следующую попытку позвонить сообщением «звонок уже идёт».
        if (createdSessionId !== null) {
          await lessonCallsApi.end(lessonId, createdSessionId).catch(() => undefined)
          sessionRef.current = null
          setSession(null)
        }
      }
    },
    [applyMediaError, enterCall, lessonId, loadConfig, teardownMedia],
  )

  const acceptCall = useCallback(async () => {
    const current = sessionRef.current
    if (!current) return

    setError(null)
    setErrorCode(null)
    let joinedSessionId: number | null = null

    try {
      const webrtcConfig = configRef.current ?? (await loadConfig())
      const joined = await lessonCallsApi.join(lessonId, current.id)
      joinedSessionId = joined.id
      setSession(joined)
      sessionRef.current = joined

      // Вход прерван — выходим, чтобы не занимать место в лимите участников.
      if (!(await enterCall(joined, webrtcConfig))) {
        await lessonCallsApi.leave(lessonId, joined.id).catch(() => undefined)
      }
    } catch (err) {
      applyMediaError(err)
      teardownMedia()
      setStatus('idle')

      // Вошли в сессию, но медиа не получили — выходим, чтобы не занимать
      // место в лимите участников «мёртвым» подключением.
      if (joinedSessionId !== null) {
        await lessonCallsApi.leave(lessonId, joinedSessionId).catch(() => undefined)
      }
    }
  }, [applyMediaError, enterCall, lessonId, loadConfig, teardownMedia])

  /**
   * Отклонить входящий. Отклонённая сессия запоминается — иначе следующее же
   * изменение состава звонка снова открыло бы диалог у того, кто отказался.
   * На индивидуальном уроке сервер закроет звонок с причиной «отклонён».
   */
  const declineCall = useCallback(() => {
    const current = sessionRef.current
    declinedSessionRef.current = current?.id ?? null
    setStatus('idle')

    if (current) {
      void lessonCallsApi.reject(lessonId, current.id).catch(() => undefined)
    }
  }, [lessonId])

  const leaveCall = useCallback(async () => {
    const current = sessionRef.current
    if (!current) return

    setStatus('ending')
    const stats = await peersRef.current.collectQualityStats()
    channelRef.current?.sendLeave(selfId)

    try {
      await lessonCallsApi.leave(lessonId, current.id, stats)
    } catch {
      // Сессию всё равно закроет планировщик по heartbeat — не показываем
      // пользователю ошибку на выходе из звонка.
    }

    teardownMedia()
    setStatus('idle')
  }, [lessonId, selfId, teardownMedia])

  const endCall = useCallback(async () => {
    const current = sessionRef.current
    if (!current) return

    setStatus('ending')
    const stats = await peersRef.current.collectQualityStats()

    try {
      await lessonCallsApi.leave(lessonId, current.id, stats)
      await lessonCallsApi.end(lessonId, current.id)
    } catch (err) {
      applyApiError(err)
    }

    teardownMedia()
    setStatus('idle')
  }, [applyApiError, lessonId, teardownMedia])

  const toggleCamera = useCallback(async () => {
    const webrtcConfig = configRef.current
    const stream = media.getStream()
    if (!stream) return

    try {
      const track = await media.toggleCamera(webrtcConfig?.video.constraints ?? null)

      if (track) {
        await peersRef.current.publishVideoTrack(track, stream)
      } else {
        await peersRef.current.replaceVideoTrack(null)
      }

      setLocalStream(stream)
    } catch (err) {
      applyMediaError(err)
    }
  }, [applyMediaError, media])

  const toggleScreenShare = useCallback(async () => {
    const stream = media.getStream()
    if (!stream) return

    try {
      const track = await media.toggleScreenShare((fallback) => {
        // Показ остановлен кнопкой браузера — возвращаем камеру (или ничего).
        void (fallback
          ? peersRef.current.publishVideoTrack(fallback, stream)
          : peersRef.current.replaceVideoTrack(null))
      })

      if (track) {
        await peersRef.current.publishVideoTrack(track, stream)
      } else {
        await peersRef.current.replaceVideoTrack(null)
      }
    } catch (err) {
      applyMediaError(err)
    }
  }, [applyMediaError, media])

  /** Реакция на состояние звонка из presence-канала урока. */
  useEffect(() => {
    if (!enabled || !callState || callState.lesson_id !== lessonId) return

    const incoming = callState.session
    setSession(incoming)
    sessionRef.current = incoming

    if (!incoming.is_active) {
      if (statusRef.current !== 'idle') {
        teardownRef.current()
      }
      setStatus('idle')
      return
    }

    const inCall = incoming.participants?.some((p) => p.user_id === selfId && p.is_active) ?? false

    // Звонок идёт, а нас в нём нет — показываем приглашение. Инициатор в этот
    // момент уже в состоянии connecting/active, поэтому себе диалог не увидит;
    // но если он вышел, а звонок продолжается, вернуться нужно тем же путём.
    if (!inCall && statusRef.current === 'idle' && declinedSessionRef.current !== incoming.id) {
      setStatus('incoming')
    }
  }, [callState, enabled, lessonId, selfId])

  /**
   * Конфиг нужен до начала звонка: по нему кнопка знает, разрешены ли режимы
   * тарифом и показывать ли выбор «аудио/видео».
   */
  useEffect(() => {
    if (!enabled) return

    let cancelled = false

    lessonCallsApi
      .getWebrtcConfig(lessonId)
      .then((loaded) => {
        if (cancelled) return
        setConfig(loaded)
        configRef.current = loaded
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [enabled, lessonId])

  /** Восстановление после перезагрузки страницы: идущий звонок не теряется. */
  useEffect(() => {
    if (!enabled) return

    let cancelled = false

    lessonCallsApi
      .getActive(lessonId)
      .then((active) => {
        // Пользователь мог начать звонок, пока запрос был в пути: перебивать
        // активный звонок диалогом входящего нельзя.
        if (cancelled || !active || statusRef.current !== 'idle') return

        setSession(active)
        sessionRef.current = active

        // Медиа после перезагрузки нет, даже если мы числимся участником:
        // показываем приглашение, чтобы вход прошёл по жесту пользователя
        // (Safari без жеста не отдаёт звук). Инициатор, вернувшийся на
        // страницу, тоже должен видеть кнопку входа, а не «Позвонить» —
        // второй звонок на уроке начать нельзя.
        setStatus('incoming')
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [enabled, lessonId])

  /** Урок перестал идти — звонок закрывается вместе с ним. */
  useEffect(() => {
    if (enabled || statusRef.current === 'idle') return

    teardownRef.current()
    setStatus('idle')
  }, [enabled])

  useEffect(() => () => teardownRef.current(), [])

  const participants = useMemo<CallParticipantView[]>(() => {
    const active = (session?.participants ?? []).filter((p) => p.is_active)

    return active.map((participant) => ({
      userId: participant.user_id,
      name: participant.user_name || channelUsers[participant.user_id] || '',
      connectionState:
        participant.user_id === selfId
          ? 'local'
          : connectionStates[participant.user_id] ?? 'new',
    }))
  }, [channelUsers, connectionStates, session, selfId])

  const value = useMemo<CallContextValue>(
    () => ({
      status,
      session,
      config,
      participants,
      remoteStreams,
      localStream,
      micEnabled: media.state.micEnabled,
      cameraEnabled: media.state.cameraEnabled,
      screenSharing: media.state.screenSharing,
      duration,
      error,
      errorCode,
      quality,
      subscribeVoiceLevels: voice.subscribe,
      startCall,
      acceptCall,
      declineCall,
      leaveCall,
      endCall: canManage ? endCall : leaveCall,
      toggleMic: media.toggleMic,
      toggleCamera,
      toggleScreenShare,
      clearError: () => {
        setError(null)
        setErrorCode(null)
      },
    }),
    [
      acceptCall,
      canManage,
      config,
      declineCall,
      duration,
      endCall,
      error,
      errorCode,
      leaveCall,
      localStream,
      voice.subscribe,
      media.state.cameraEnabled,
      media.state.micEnabled,
      media.state.screenSharing,
      media.toggleMic,
      participants,
      quality,
      remoteStreams,
      session,
      startCall,
      status,
      toggleCamera,
      toggleScreenShare,
    ],
  )

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>
}
